// Dependency-free i18n. A single `Dict` shape is implemented per language; the
// active language is persisted to localStorage and detected from the browser on
// first run. Strings that take parameters or pluralize are functions, so the
// whole surface stays type-checked — a missing or mis-typed key fails the build.
//
// Proper nouns (driver names, constructors, country codes, track names) are
// never translated; only the surrounding prose is. Synergy reasons/tags arrive
// as structured data from the engine and are formatted here, so a season drafted
// in one language renders correctly after switching to another.
import type { BadgeGroup, BadgeId, DirectorTrait, Rarity, SynergyReason, SynergyTag, Weather } from "../types";

export type Lang = "en" | "pt-BR";
export const LANGS: readonly Lang[] = ["en", "pt-BR"];
export const LANG_LABEL: Record<Lang, string> = { en: "EN", "pt-BR": "PT-BR" };

// Card attributes: the three driver stats plus the two chassis stats. Each is
// shown as a short abbreviation with the full name on hover.
export type AttrKey = "pace" | "consistency" | "rain" | "motor" | "aero";
export interface AttrLabel {
  readonly abbr: string;
  readonly full: string;
}

export interface Dict {
  readonly status: string;
  readonly disclaimer: string;

  // Menu
  readonly menuTitle: string;
  readonly menuSub: string;
  readonly startTitle: string;
  readonly cost: string;
  // Home-screen mode split: Daily Challenge (seeded, shared) vs Free Season (random).
  readonly dailyTitle: string;
  readonly dailyCost: string;
  readonly dailyPlayed: (pos: number) => string;
  readonly dailyFailed: string;
  readonly dailyForfeitConfirm: string;
  readonly forfeit: string;
  readonly dailyLocked: (chaos: boolean) => string;
  readonly freeTitle: string;
  readonly freeCost: string;
  readonly menuHint: string;
  readonly menuHowToNudge: string;
  readonly pastSeasons: string;
  readonly thWdc: string;
  readonly thWcc: string;
  readonly resetProgress: string;
  readonly resetConfirm: string;
  // Shared confirm-dialog buttons (reset progress, start over).
  readonly confirmYes: string;
  readonly confirmCancel: string;

  // Suggestions (menu overlay -> POST /api/suggest)
  readonly suggestNav: string;
  readonly suggestTitle: string;
  readonly suggestSub: string;
  readonly suggestPlaceholder: string;
  readonly suggestSend: string;
  readonly suggestSending: string;
  readonly suggestThanks: string;
  readonly suggestError: string;
  readonly suggestEmpty: string;

  // Badges
  readonly badgesNav: string;
  readonly badgesTitle: string;
  readonly badgesSub: string;
  readonly badgeUnlocked: (name: string) => string;
  readonly badgeProgress: (current: number, target: number) => string;
  readonly badgeName: (id: BadgeId) => string;
  readonly badgeDesc: (id: BadgeId) => string;
  readonly badgeGroupName: (group: BadgeGroup) => string;
  readonly shareBadges: string;

  // How to play (first-run overlay + menu button)
  readonly howToNav: string;
  readonly howToTitle: string;
  readonly howToSteps: readonly { readonly h: string; readonly p: string }[];
  readonly howToCta: string;

  // Naming
  readonly nameTitle: string;
  readonly nameSub: string;
  readonly namePlaceholder: string;
  readonly nameHint: string;
  readonly chaosMode: string;
  readonly chaosDesc: (chaos: number, calm: number) => string;
  readonly memoryMode: string;
  readonly memoryDesc: string;
  readonly startDrafting: string;
  readonly back: string;
  readonly teamLabel: string;   // menu: "Team: <name>"
  readonly editName: string;    // menu button
  readonly nameSave: string;    // rename modal save button

  // Draft
  readonly driver1: string;
  readonly driver2: string;
  readonly chassis: string;
  readonly attrs: Record<AttrKey, AttrLabel>;
  readonly empty: string;
  readonly premiumPack: string;
  readonly normalPack: string;
  readonly draftMeta: (pct: number, mythics: number) => string;
  readonly rollTitle: (n: number) => string;
  readonly keepOne: (need: string) => string;
  readonly needDriverOrChassis: string;
  readonly needDrivers: (n: number) => string;
  readonly needChassis: string;
  readonly fromMemoryBadge: string;
  readonly rerollFive: (n: number) => string;
  readonly startOver: string;
  readonly startOverConfirm: string;
  readonly lockHint: string;
  readonly keepThis: string;
  readonly slotFull: string;
  readonly rerollByTeam: (v: string) => string;
  readonly rerollByCountry: (v: string) => string;
  readonly chassisMeta: string;

  // Director draft (the mandatory 4th pick)
  readonly director: string;
  readonly directorTitle: string;
  readonly directorSub: string;
  readonly directorIndependent: string; // shown for directors with no constructor
  readonly directorName: (name: string) => string; // translate unlicensed generics; pass real names through
  readonly directorTrait: (tr: DirectorTrait) => string;
  readonly directorTraitTip: (tr: DirectorTrait) => string; // hover: explains the archetype
  readonly directorAttrs: { readonly reliability: AttrLabel; readonly development: AttrLabel };
  readonly directorAttrTip: (key: "reliability" | "development") => string; // hover: explains the stat
  // Live preview of a director's concrete effect on the player's actual lineup,
  // shown as chips on the draft card. Each takes the measured delta.
  readonly directorPreviewTitle: string;
  readonly directorPreviewScore: (n: number) => string;   // dry score per car
  readonly directorPreviewWet: (n: number) => string;     // extra in the wet
  readonly directorPreviewDnf: (n: number) => string;     // DNF risk, in points
  readonly directorPreviewLuck: (pct: number) => string;  // luck swing change, signed %
  readonly directorPreviewCar: (motor: number, aero: number) => string; // home-car development buff
  readonly directorPreviewNone: string;                   // no notable effect on this lineup
  readonly directorPreviewTip: string;                    // hover: explains the preview
  // Affinity chip in the "on your lineup" preview: home-team boost vs an independent's any-chassis edge.
  readonly directorPreviewAffinity: (pct: number, home: boolean) => string;
  // Tooltip on the affinity tag where it also appears on the season lineup panel.
  readonly directorAffinityTip: (home: boolean) => string;
  // Calendar shown during the director pick, so the player can weigh a
  // qualifying specialist (low-pace circuits) vs a race strategist (power).
  readonly calendarTitle: string;
  readonly calendarLegend: string;
  readonly paceTierLabel: (tier: "low" | "power" | "balanced") => string;
  // Per-chip tooltip on the calendar, explaining what the chip's COLOUR (pace
  // tier) means — kept on the same axis as the colour so the two never clash.
  readonly paceTierTip: (tier: "low" | "power" | "balanced") => string;
  readonly rerollDirectors: (n: number) => string;
  readonly keepDirector: string;

  // Season
  readonly seasonRound: (r: number) => string;
  readonly nextGp: string;
  readonly projected: (track: string, d1: string, p1: number, d2: string, p2: number, swing: number, chaos: boolean) => string;
  readonly runGp: (track: string) => string;
  readonly abandon: string;
  readonly viewChampionship: string;
  readonly challengesTitle: string;
  readonly yourLineup: string;
  readonly lineupHint: string;
  readonly lineupShow: string;
  readonly lineupHide: string;
  readonly driver: string;
  readonly noSynergies: string;
  readonly fromSynergy: string;
  readonly fromHistoric: string;
  readonly fromCarDev: string;
  readonly thPos: string;
  readonly thPosShort: string;
  readonly thEntry: string;
  readonly thConstructor: string;
  readonly thPts: string;
  readonly thDriver: string;
  readonly thLineup: string;
  readonly dnf: string;
  readonly yourResult: (pos: string, driver: string, points: number) => string;
  readonly standingsTitle: string;
  readonly tabConstructors: string;
  readonly tabDrivers: string;

  // Result
  readonly wccChampion: string;
  readonly wdcChampion: string;
  readonly titleSecured: string;
  readonly noTitle: string;
  readonly memoryWin: string;
  readonly newSeason: string;
  readonly shareResult: string;
  readonly sharePreparing: string;
  readonly shareSaved: string;
  readonly shareShared: string;
  readonly shareUnavailable: string;
  readonly shareModalTitle: string;
  readonly shareVia: string;
  readonly shareCopy: string;
  readonly shareCopied: string;
  readonly shareCopyFailed: string;
  readonly shareDownload: string;
  readonly shareClose: string;

  // Share image
  readonly imgSeasonComplete: string;
  readonly imgDaily: (day: string) => string; // top-right tag for a Daily Challenge result
  readonly imgWccChampion: string;
  readonly imgWdcChampion: string;
  readonly imgTitleSecured: string;
  readonly imgNoTitle: string;
  readonly imgMemoryWin: string;
  readonly imgChaosWin: string;
  readonly imgPositions: (wccPos: number, pts: number) => string;
  readonly imgSynergies: string;
  readonly imgFooter: string;
  readonly imgEmblems: string;
  readonly imgEmblemsCount: (n: number, total: number) => string;
  readonly shareBadgesCaption: (n: number, total: number) => string;
  readonly shareCaptionWon: (team: string) => string;
  readonly shareCaptionLost: (team: string) => string;
  // Daily Challenge share: a text scoreboard (no lineup) + challenge, shared
  // from a dedicated text modal so a friend can't copy the player's picks.
  readonly shareCaptionDaily: (team: string, won: boolean, wccPos: number, teamPts: number, driverPts: string) => string;
  readonly shareDailyTitle: string;
  readonly shareCopyMessage: string;

  // Daily leaderboard: result-screen panel + the one-time privacy notice shown
  // the first time a result is auto-posted (the team name becomes public).
  readonly lbTitle: string;
  readonly lbLoading: string;
  readonly lbEmpty: string;
  readonly lbYou: string;
  readonly lbTeam: string;
  readonly lbNoticeTitle: string;
  readonly lbNoticeBody: string;
  readonly lbNoticeOptOut: string;
  readonly lbNoticeOk: string;

  // Structured formatters
  readonly rarity: (r: Rarity) => string;
  readonly synReason: (r: SynergyReason) => string;
  readonly synTag: (t: SynergyTag) => string;
  readonly trackLabel: (label: string) => string;
  readonly weather: (w: Weather) => string;
  readonly traitTip: (key: "motor" | "aero" | "pace" | "consistency" | "balanced") => string;
  readonly weatherTip: (w: Weather) => string;
}

const luckTag = (chaos: boolean, en: boolean): string =>
  chaos ? (en ? "chaos" : "caos") : en ? "calm" : "calmo";

/** Render a number with an explicit sign, e.g. 3 -> "+3", -2 -> "−2". */
const signed = (n: number): string => (n > 0 ? `+${n}` : n < 0 ? `−${Math.abs(n)}` : `${n}`);

const en: Dict = {
  status: "Roguelike season sim",
  disclaimer:
    "Unofficial fan project — not affiliated with or endorsed by Formula 1, the FIA, or any team. All names are the property of their respective owners.",

  menuTitle: "New season draft",
  menuSub:
    "Draft two drivers, a chassis and a team director from card packs — then race a 10-Grand-Prix season for the title.",
  startTitle: "Start the draft",
  cost: "Free · pick your modes next",
  dailyTitle: "Daily Challenge",
  dailyCost: "Today's seed — same draft for everyone →",
  dailyPlayed: (pos) => `Played today — P${pos} · view result →`,
  dailyFailed: "Forfeited today — back tomorrow",
  dailyForfeitConfirm:
    "Leave now and today's Daily Challenge counts as failed — the seed is one-and-done, so you can't retry until tomorrow. Continue?",
  forfeit: "Forfeit",
  dailyLocked: (chaos) => `Today: Chaos ${chaos ? "on" : "off"} · From Memory off (set by the seed)`,
  freeTitle: "Free Season",
  freeCost: "Draft a random team, anytime →",
  menuHint: "Each draft is free. Even a perfect lineup loses titles regularly.",
  menuHowToNudge: "New here? Tap “How to play”.",
  pastSeasons: "Past seasons",
  thWdc: "WDC",
  thWcc: "WCC",
  resetProgress: "Reset progress",
  resetConfirm: "Reset all progress?",
  confirmYes: "Confirm",
  confirmCancel: "Cancel",

  suggestNav: "Suggestions",
  suggestTitle: "Send a suggestion",
  suggestSub:
    "Got an idea, a driver you miss, or a bug? Tell us — it lands straight in our inbox.",
  suggestPlaceholder: "Your suggestion…",
  suggestSend: "Send",
  suggestSending: "Sending…",
  suggestThanks: "Thanks! Your suggestion is on its way.",
  suggestError: "Couldn't send right now. Please try again later.",
  suggestEmpty: "Write something first.",

  badgesNav: "Badges",
  badgesTitle: "Badges",
  badgesSub: "Permanent unlocks — earned once, kept forever.",
  badgeUnlocked: (name) => `Badge unlocked: ${name}`,
  shareBadges: "⤴ Share emblems",

  howToNav: "How to play",
  howToTitle: "How to play",
  howToSteps: [
    {
      h: "1 · Draft your team",
      p: "Three rolls: each opens a 5-card pack — keep one, then roll again. Collect 2 drivers and 1 chassis. 4 rerolls let you reshape a pack (or a single card) anytime.",
    },
    {
      h: "2 · Build synergy",
      p: "A driver in a car they actually raced gains pace & consistency; two drivers of the same nation or real ex-teammates gain consistency. Green tags show synergies as you pick — a coherent lineup beats a pile of stars.",
    },
    {
      h: "3 · Hire a director",
      p: "The mandatory 4th pick. Each principal has a signature — race pace, reliability, synergy amplification, high-risk gamble, wet-weather, development. On the chassis of the constructor they ran, they also develop the car itself (+motor & aero) — so matching your director to your chassis pays off.",
    },
    {
      h: "4 · Race for the title",
      p: "10 Grands Prix run automatically. Each track favours different stats and weather can strike. Beat a grid of historic rival teams to the Constructors' and Drivers' titles — and chase {badgeCount} badges.",
    },
    {
      h: "5 · Daily or Free Season",
      p: "Daily Challenge: the same draft and races for everyone today — one run, then share your scoreboard to challenge friends to beat it. Free Season: a fresh random draft anytime, replay as much as you like.",
    },
  ],
  howToCta: "Let's race",
  badgeProgress: (current, target) => `${current}/${target}`,
  badgeName: (id) => {
    switch (id) {
      case "wcc_champion":
        return "Constructors' Champion";
      case "wdc_champion":
        return "Drivers' Champion";
      case "chaos_champion":
        return "Chaos Champion";
      case "memory_champion":
        return "From-Memory Champion";
      case "no_mythic_champion":
        return "Underdog";
      case "rain_master":
        return "Rain Master";
      case "perfect_lineup":
        return "Perfect Lineup";
      case "grand_slam":
        return "Grand Slam";
      case "king_of_the_midfield":
        return "King of the Midfield";
      case "one_two":
        return "One-Two";
      case "total_dominance":
        return "Total Dominance";
      case "unbreakable":
        return "Unbreakable";
      case "national_pride":
        return "National Pride";
      case "late_charge":
        return "Late Charge";
      case "pace_master":
        return "Pace Master";
      case "perfect_harmony":
        return "Perfect Harmony";
      case "goat":
        return "The G.O.A.T.";
      case "nation_bra":
        return "Verde-Amarela";
      case "nation_gbr":
        return "Rule Britannia";
      case "nation_ger":
        return "Silver Arrows";
      case "nation_fra":
        return "Les Bleus";
      case "nation_arg":
        return "El Maestro's Heirs";
      case "nation_esp":
        return "La Furia Roja";
      case "nation_fin":
        return "Flying Finns";
      case "nation_aus":
        return "Aussie Grit";
      case "nation_aut":
        return "The Rat Pack";
      case "nation_ita":
        return "Forza Azzurri";
      case "nation_mex":
        return "Viva México";
      case "nation_can":
        return "The Maple Leaf";
    }
  },
  badgeDesc: (id) => {
    switch (id) {
      case "wcc_champion":
        return "Win the Constructors' Championship";
      case "wdc_champion":
        return "Win the Drivers' Championship";
      case "chaos_champion":
        return "Win a title with Chaos mode on";
      case "memory_champion":
        return "Win a title in From Memory mode";
      case "no_mythic_champion":
        return "Win a title without picking a single Mythic card";
      case "rain_master":
        return "Win 2 races in wet weather in one season";
      case "perfect_lineup":
        return "Field a lineup with 3 or more active synergies at once";
      case "grand_slam":
        return "Win both the Constructors' and Drivers' titles in the same season";
      case "king_of_the_midfield":
        return "Win a championship with a Normal-rarity director or chassis";
      case "one_two":
        return "Lock out P1 and P2 with both your cars in a single race";
      case "total_dominance":
        return "Win the Constructors' title with your drivers finishing P1 and P2 in the standings";
      case "unbreakable":
        return "Win the Constructors' title without a single DNF from either of your cars all season";
      case "national_pride":
        return "Win the Constructors' title with both drivers from the same nation";
      case "late_charge":
        return "Win the final 2 Grands Prix of the season";
      case "pace_master":
        return "Win 4 Grands Prix in a single season";
      case "perfect_harmony":
        return "Win a championship with 3 or more active synergies at once";
      case "goat":
        return "Win both championships in one season with Chaos and From Memory modes on at once";
      case "nation_bra":
        return "Win the Constructors' title with two Brazilian drivers";
      case "nation_gbr":
        return "Win the Constructors' title with two British drivers";
      case "nation_ger":
        return "Win the Constructors' title with two German drivers";
      case "nation_fra":
        return "Win the Constructors' title with two French drivers";
      case "nation_arg":
        return "Win the Constructors' title with two Argentine drivers";
      case "nation_esp":
        return "Win the Constructors' title with two Spanish drivers";
      case "nation_fin":
        return "Win the Constructors' title with two Finnish drivers";
      case "nation_aus":
        return "Win the Constructors' title with two Australian drivers";
      case "nation_aut":
        return "Win the Constructors' title with two Austrian drivers";
      case "nation_ita":
        return "Win the Constructors' title with two Italian drivers — no Legends or Mythics race for Italy";
      case "nation_mex":
        return "Win the Constructors' title with two Mexican drivers — no Legends or Mythics race for Mexico";
      case "nation_can":
        return "Win the Constructors' title with two Canadian drivers — no Legends or Mythics race for Canada";
    }
  },
  badgeGroupName: (group) => {
    switch (group) {
      case "championship":
        return "Championships";
      case "mastery":
        return "Mastery";
      case "nation":
        return "Nations";
    }
  },

  nameTitle: "Name your team",
  nameSub: "This is the constructor you'll race to the championship.",
  namePlaceholder: "Apex Racing",
  nameHint: "Up to 28 characters. Leave blank for a default.",
  chaosMode: "Chaos mode",
  chaosDesc: (chaos, calm) => `— wild ±${chaos}% luck swings each race. Off = skill-dominant ±${calm}%.`,
  memoryMode: "From Memory mode",
  memoryDesc: "— hide card attributes during the draft; pick from your racing knowledge.",
  startDrafting: "Start drafting →",
  back: "Back",
  teamLabel: "Team",
  editName: "Edit name",
  nameSave: "Save",

  driver1: "Driver 1",
  driver2: "Driver 2",
  chassis: "Chassis",
  attrs: {
    pace: { abbr: "PAC", full: "Pace" },
    consistency: { abbr: "CON", full: "Consistency" },
    rain: { abbr: "RAN", full: "Rain" },
    motor: { abbr: "MOT", full: "Motor" },
    aero: { abbr: "AER", full: "Aero" },
  },
  empty: "— empty —",
  premiumPack: "PREMIUM PACK",
  normalPack: "NORMAL PACK",
  draftMeta: (pct, mythics) => `${pct}% premium this roll · ${mythics} mythic${mythics === 1 ? "" : "s"} kept`,
  rollTitle: (n) => `Roll ${n}`,
  keepOne: (need) => `Keep one card. You still need ${need}.`,
  needDriverOrChassis: "a driver or a chassis",
  needDrivers: (n) => `${n} more driver${n > 1 ? "s" : ""}`,
  needChassis: "a chassis",
  fromMemoryBadge: "FROM MEMORY",
  rerollFive: (n) => `↻ Reroll these five (${n})`,
  startOver: "Start over",
  startOverConfirm: "Start over? Your current draft will be discarded.",
  lockHint: "Reroll the whole pack, or reroll from a card to keep its team or country.",
  keepThis: "Keep this",
  slotFull: "Slot full",
  rerollByTeam: (v) => `↻ Reroll by team: ${v}`,
  rerollByCountry: (v) => `↻ Reroll by country: ${v}`,
  chassisMeta: "Chassis",

  director: "Director",
  directorTitle: "Pick your team principal",
  directorSub:
    "Your lineup is set. Choose a director — the final card — to shape how the whole team performs.",
  directorIndependent: "Independent",
  directorName: (name) =>
    ({
      "Technical Chief": "Technical Chief",
    })[name] ?? name,
  directorTrait: (tr) => {
    switch (tr.kind) {
      case "strategist":
        return `Strategist — +${tr.paceBonus} race pace`;
      case "qualifier":
        return `Qualifier — +${tr.gridBonus} grid every track (best on low-pace circuits)`;
      case "engineer":
        return `Engineer — cuts DNF risk by ${Math.round(tr.dnfCut * 100)} pts (more in the wet)`;
      case "motivator":
        return `Motivator — amplifies synergy ×${tr.synergyMult}`;
      case "gambler":
        return `Gambler — luck ×${tr.luckMult}, +${tr.scoreBonus} base (high risk)`;
      case "rainMaster":
        return `Rain master — +${tr.wetBonus} in the wet & keeps the car on track`;
      case "developer":
        return `Developer — grows up to +${tr.rampMax} across the season`;
      case "underdog":
        return `Underdog — synergy ×${tr.synergyMult} with ${tr.maxMythics} mythic or fewer`;
    }
  },
  directorTraitTip: (tr) => {
    switch (tr.kind) {
      case "strategist":
        return "Strategist — sharper race calls lift the team's pace; the gain scales with how much the track rewards pace (big on Spa/Monza, small on Monaco).";
      case "qualifier":
        return "Qualifier — a flat grid-position boost that doesn't fade on low-pace street/aero circuits, where track position beats raw pace. Out-scores a race strategist on a street-heavy calendar.";
      case "engineer":
        return "Engineer — a reliability focus that cuts the team's retirement risk in all conditions, especially in the wet.";
      case "motivator":
        return "Motivator — gets more out of a lineup that already has synergy.";
      case "gambler":
        return "Gambler — bigger luck swings plus a base boost: high risk, high reward.";
      case "rainMaster":
        return "Rain master — a big score boost in the wet and far fewer wet retirements; transforms rainy races.";
      case "developer":
        return "Developer — the car keeps improving as the 10-race season goes on.";
      case "underdog":
        return "Underdog — rewards a cheap, well-gelled roster with few or no mythics.";
    }
  },
  directorAttrs: {
    reliability: { abbr: "REL", full: "Reliability" },
    development: { abbr: "DEV", full: "Development" },
  },
  directorAttrTip: (key) =>
    key === "reliability"
      ? "Reliability — steadies the team against luck swings and retirements. Negative for aggressive directors who trade it for pace."
      : "Development — how much the team improves across the 10-race season.",
  directorPreviewTitle: "On your lineup",
  directorPreviewScore: (n) => `⚡ ${signed(n)} score`,
  directorPreviewWet: (n) => `🌧 +${n} in the wet`,
  directorPreviewDnf: (n) => `🛡 ${signed(n)}% DNF`,
  directorPreviewLuck: (pct) => `🎲 ${signed(pct)}% luck swing`,
  directorPreviewCar: (motor, aero) => `🔧 +${motor} MOT +${aero} AER home car`,
  directorPreviewNone: "no measurable effect on this lineup",
  directorPreviewTip:
    "Measured effect of this principal on YOUR two drivers + chassis, vs. running no director — pace per car, wet-weather bonus, change in retirement risk, and how much the luck swing widens or narrows. Any home/independent affinity is already baked into these numbers.",
  directorPreviewAffinity: (pct, home) => (home ? `◇ +${pct}% home car` : `◇ +${pct}% any car`),
  directorAffinityTip: (home) =>
    home
      ? "Home machinery — this principal ran this constructor: their performance levers get the full home-team boost and they develop the car itself (+motor & aero)."
      : "Independent — no home constructor, but a smaller edge that applies on any chassis.",
  calendarTitle: "Season calendar",
  calendarLegend: "Low-pace circuits reward a qualifier; power circuits reward a race strategist.",
  paceTierLabel: (tier) => (tier === "low" ? "low-pace" : tier === "power" ? "power" : "balanced"),
  paceTierTip: (tier) =>
    tier === "low"
      ? "Low-pace circuit — track position is king, so a qualifying specialist shines here."
      : tier === "power"
        ? "Power circuit — raw pace matters most, so a race strategist's pace boost pays off."
        : "Balanced circuit — no single specialist has a clear edge.",
  rerollDirectors: (n) => `↻ Reroll directors (${n})`,
  keepDirector: "Hire this director",

  seasonRound: (r) => `Season — round ${r} / 10`,
  nextGp: "Next Grand Prix",
  projected: (track, d1, p1, d2, p2, swing, chaos) =>
    `Projected at ${track}: ${d1} ${p1} · ${d2} ${p2} · luck ±${swing}% (${luckTag(chaos, true)})`,
  runGp: (track) => `▸ Run ${track} GP`,
  abandon: "Abandon",
  viewChampionship: "View championship →",
  challengesTitle: "Challenges in progress",
  yourLineup: "Your lineup",
  lineupHint: "— green stats include synergy bonuses",
  lineupShow: "Stats ▾",
  lineupHide: "Hide ▴",
  driver: "Driver",
  noSynergies: "No active synergies for this lineup.",
  fromSynergy: "from synergy",
  fromHistoric: "from the historic drive",
  fromCarDev: "director develops the car",
  thPos: "Pos",
  thPosShort: "P",
  thEntry: "Entry",
  thConstructor: "Constructor",
  thPts: "Pts",
  thDriver: "Driver",
  thLineup: "Lineup",
  dnf: "DNF",
  yourResult: (pos, driver, points) => `Your result: ${pos} — ${driver}${points ? ` · ${points} pts` : ""}`,
  standingsTitle: "Championship standings",
  tabConstructors: "Constructors",
  tabDrivers: "Drivers",

  wccChampion: "World Champion Constructor",
  wdcChampion: "World Champion Driver",
  titleSecured: "Title secured — added to your history.",
  noTitle: "No title this time — run it back.",
  memoryWin: "🧠 From Memory champion — you won with every card stat hidden!",
  newSeason: "New season →",
  shareResult: "⤴ Share result",
  sharePreparing: "Preparing image…",
  shareSaved: "Image saved ✓",
  shareShared: "Shared ✓",
  shareUnavailable: "Share unavailable",
  shareModalTitle: "Your season",
  shareVia: "⤴ Share…",
  shareCopy: "⧉ Copy image",
  shareCopied: "Copied ✓",
  shareCopyFailed: "Copy failed",
  shareDownload: "↓ Download",
  shareClose: "Close",

  imgSeasonComplete: "SEASON COMPLETE",
  imgDaily: (day) => `DAILY · ${day}`,
  imgWccChampion: "WORLD CHAMPION CONSTRUCTOR",
  imgWdcChampion: "WORLD CHAMPION DRIVER",
  imgTitleSecured: "★ TITLE SECURED ★",
  imgNoTitle: "NO TITLE THIS TIME",
  imgMemoryWin: "🧠 FROM MEMORY CHAMPION",
  imgChaosWin: "⚡ CHAOS MODE CHAMPION",
  imgPositions: (wccPos, pts) => `Constructors P${wccPos} · ${pts} pts`,
  imgSynergies: "SYNERGIES",
  imgFooter: "Draft a legendary Grand Prix season — play your own",
  imgEmblems: "MY EMBLEMS",
  imgEmblemsCount: (n, total) => `${n} of ${total} unlocked`,
  shareBadgesCaption: (n, total) => `I've unlocked ${n}/${total} emblems in Grand Prix Draft Legends! 🏅`,
  shareCaptionWon: (team) => `${team} are World Champions in Grand Prix Draft Legends! 🏆`,
  shareCaptionLost: (team) => `My Grand Prix Draft Legends season with ${team}.`,
  shareCaptionDaily: (team, won, wccPos, teamPts, driverPts) =>
    won
      ? `${team} won today's Grand Prix Draft Legends Daily Challenge! 🏆 Constructors P${wccPos}, ${teamPts} pts (drivers ${driverPts} pts). Same draft for everyone — think you can beat that?`
      : `${team} finished Constructors P${wccPos} (${teamPts} pts · drivers ${driverPts} pts) in today's Grand Prix Draft Legends Daily Challenge. Same draft for everyone — think you can do better?`,
  shareDailyTitle: "Challenge a friend",
  shareCopyMessage: "⧉ Copy message",

  lbTitle: "Daily leaderboard",
  lbLoading: "Loading today's times…",
  lbEmpty: "No scores posted yet — be the first.",
  lbYou: "You",
  lbTeam: "Team",
  lbNoticeTitle: "Posted to the leaderboard",
  lbNoticeBody:
    "Your daily result and team name now appear on the public daily leaderboard. You can stop posting future results any time.",
  lbNoticeOptOut: "Don't post my results",
  lbNoticeOk: "Got it",

  rarity: (r) => r,
  synReason: (r) => {
    switch (r.kind) {
      case "historicDrive":
        return `${r.driver} ↔ ${r.team} (historic drive)`;
      case "sameNation":
        return `Same nation (${r.country}) — teammate chemistry`;
      case "sharedTeam":
        return `${r.a} & ${r.b} shared a team historically`;
    }
  },
  synTag: (t) => {
    switch (t.kind) {
      case "droveFor":
        return `Drove for ${t.team}`;
      case "sameNationAs":
        return `Same nation as ${t.driver} (${t.country})`;
      case "exTeammate":
        return `Ex-teammate of ${t.driver}`;
      case "chassisDroveFor":
        return `${t.driver} drove for ${t.team}`;
    }
  },
  trackLabel: (label) => label,
  weather: (w) => (w === "heavy_rain" ? "Heavy rain" : w === "light_rain" ? "Light rain" : "Dry"),
  traitTip: (key) => {
    switch (key) {
      case "motor":
        return "Engine power matters most here";
      case "aero":
        return "Aerodynamics/downforce matters most here";
      case "pace":
        return "Driver pace matters most here";
      case "consistency":
        return "Driver consistency matters most here";
      case "balanced":
        return "Balanced — no single trait dominates";
    }
  },
  weatherTip: (w) =>
    w === "heavy_rain"
      ? "Heavy rain — rain skill matters a lot, retirement risk is high"
      : w === "light_rain"
        ? "Light rain — rain skill starts to matter, retirement risk rises a little"
        : "Dry conditions — rain skill has no effect",
};

const TRACKS_PT: Record<string, string> = {
  "Aero · slow corners": "Aero · curvas lentas",
  "Balanced · fast": "Equilibrado · rápido",
  "Motor + pace": "Motor + ritmo",
  "Pure power": "Potência pura",
  "Mixed consistency": "Consistência mista",
  "Technical · aero": "Técnico · aero",
  "Stop-go · motor": "Anda-e-para · motor",
  "Aero · twisty": "Aero · sinuoso",
  "Old-school · consistency": "Clássico · consistência",
  "Balanced finale": "Final equilibrado",
  "High-speed street": "Rua de alta velocidade",
  "Street · endurance": "Rua · resistência",
  "Balanced · technical": "Equilibrado · técnico",
  "Stop-go · traction": "Anda-e-para · tração",
  "Banked · aero": "Inclinado · aero",
  "Technical · all-round": "Técnico · completo",
};

const ptBR: Dict = {
  status: "Sim. de temporada roguelike",
  disclaimer:
    "Projeto de fã não-oficial — sem afiliação ou endosso da Formula 1, da FIA ou de qualquer equipe. Todos os nomes pertencem aos seus respectivos donos.",

  menuTitle: "Draft de nova temporada",
  menuSub:
    "Drafte dois pilotos, um chassi e um diretor de equipe a partir de pacotes de cartas — e dispute uma temporada de 10 Grandes Prêmios pelo título.",
  startTitle: "Começar o draft",
  cost: "Grátis · escolha seus modos a seguir",
  dailyTitle: "Desafio Diário",
  dailyCost: "Grid de hoje — o mesmo para todos →",
  dailyPlayed: (pos) => `Jogado hoje — P${pos} · ver resultado →`,
  dailyFailed: "Desistiu hoje — volte amanhã",
  dailyForfeitConfirm:
    "Sair agora faz o Desafio Diário de hoje contar como falha — a seed é única por dia, então você só poderá tentar de novo amanhã. Continuar?",
  forfeit: "Desistir",
  dailyLocked: (chaos) => `Hoje: Caos ${chaos ? "ligado" : "desligado"} · Modo Almanaque desligado (definido pela seed)`,
  freeTitle: "Temporada Livre",
  freeCost: "Monte uma equipe aleatória quando quiser →",
  menuHint: "Todo draft é grátis. Até uma equipe perfeita perde títulos com frequência.",
  menuHowToNudge: "Novo por aqui? Toque em “Como jogar”.",
  pastSeasons: "Temporadas anteriores",
  thWdc: "PIL",
  thWcc: "CON",
  resetProgress: "Zerar progresso",
  resetConfirm: "Zerar todo o progresso?",
  confirmYes: "Confirmar",
  confirmCancel: "Cancelar",

  suggestNav: "Sugestões",
  suggestTitle: "Enviar sugestão",
  suggestSub:
    "Tem uma ideia, sente falta de um piloto ou achou um bug? Conta pra gente — vai direto pra nossa caixa de entrada.",
  suggestPlaceholder: "Sua sugestão…",
  suggestSend: "Enviar",
  suggestSending: "Enviando…",
  suggestThanks: "Valeu! Sua sugestão está a caminho.",
  suggestError: "Não foi possível enviar agora. Tente de novo mais tarde.",
  suggestEmpty: "Escreva algo primeiro.",

  badgesNav: "Emblemas",
  badgesTitle: "Emblemas",
  badgesSub: "Conquistas permanentes — ganhe uma vez, guarde para sempre.",
  badgeUnlocked: (name) => `Emblema desbloqueado: ${name}`,
  shareBadges: "⤴ Compartilhar emblemas",

  howToNav: "Como jogar",
  howToTitle: "Como jogar",
  howToSteps: [
    {
      h: "1 · Monte seu time",
      p: "Três rodadas: cada uma abre um pacote de 5 cartas — guarde uma e role de novo. Junte 2 pilotos e 1 chassi. 4 rerolls remodelam um pacote (ou uma carta só) a qualquer momento.",
    },
    {
      h: "2 · Crie sinergia",
      p: "Um piloto no carro que ele pilotou de verdade ganha ritmo & consistência; dois pilotos da mesma nação ou ex-companheiros reais ganham consistência. Tags verdes mostram as sinergias ao escolher — um time coeso vence uma pilha de estrelas.",
    },
    {
      h: "3 · Contrate um diretor",
      p: "O 4º pick obrigatório. Cada chefe tem uma assinatura — ritmo, confiabilidade, amplificar sinergia, aposta de risco, chuva, desenvolvimento. No chassi da construtora que ele comandou, ele também desenvolve o próprio carro (+motor & aero) — então casar o diretor com o chassi compensa.",
    },
    {
      h: "4 · Corra pelo título",
      p: "10 Grands Prix rodam sozinhos. Cada pista favorece stats diferentes e o clima pode atacar. Vença um grid de rivais históricos pelos títulos de Construtores e Pilotos — e cace os {badgeCount} emblemas.",
    },
    {
      h: "5 · Diário ou Temporada Livre",
      p: "Desafio Diário: o mesmo grid e as mesmas corridas para todos hoje — uma tentativa, depois compartilhe seu placar para desafiar amigos a superá-lo. Temporada Livre: um draft aleatório quando quiser, jogue quantas vezes quiser.",
    },
  ],
  howToCta: "Bora correr",
  badgeProgress: (current, target) => `${current}/${target}`,
  badgeName: (id) => {
    switch (id) {
      case "wcc_champion":
        return "Campeão de Construtores";
      case "wdc_champion":
        return "Campeão de Pilotos";
      case "chaos_champion":
        return "Campeão do Caos";
      case "memory_champion":
        return "Campeão de Almanaque";
      case "no_mythic_champion":
        return "Azarão";
      case "rain_master":
        return "Mestre da Chuva";
      case "perfect_lineup":
        return "Equipe Perfeita";
      case "grand_slam":
        return "Grand Slam";
      case "king_of_the_midfield":
        return "Rei do Pelotão Intermediário";
      case "one_two":
        return "Dobradinha";
      case "total_dominance":
        return "Domínio Total";
      case "unbreakable":
        return "Inquebrável";
      case "national_pride":
        return "Orgulho Nacional";
      case "late_charge":
        return "Arrancada Final";
      case "pace_master":
        return "Ritmo Implacável";
      case "perfect_harmony":
        return "Harmonia Perfeita";
      case "goat":
        return "The G.O.A.T.";
      case "nation_bra":
        return "Verde-Amarela";
      case "nation_gbr":
        return "Rule Britannia";
      case "nation_ger":
        return "Silver Arrows";
      case "nation_fra":
        return "Les Bleus";
      case "nation_arg":
        return "Herdeiros do Maestro";
      case "nation_esp":
        return "La Furia Roja";
      case "nation_fin":
        return "Finlandeses Voadores";
      case "nation_aus":
        return "Aussie Grit";
      case "nation_aut":
        return "The Rat Pack";
      case "nation_ita":
        return "Forza Azzurri";
      case "nation_mex":
        return "Viva México";
      case "nation_can":
        return "A Folha de Bordo";
    }
  },
  badgeDesc: (id) => {
    switch (id) {
      case "wcc_champion":
        return "Vença o Campeonato de Construtores";
      case "wdc_champion":
        return "Vença o Campeonato de Pilotos";
      case "chaos_champion":
        return "Vença um título com o Modo Caos ativado";
      case "memory_champion":
        return "Vença um título no Modo Almanaque";
      case "no_mythic_champion":
        return "Vença um título sem escolher nenhuma carta mítica";
      case "rain_master":
        return "Vença 2 corridas sob chuva em uma temporada";
      case "perfect_lineup":
        return "Monte uma equipe com 3 ou mais sinergias ativas ao mesmo tempo";
      case "grand_slam":
        return "Vença os títulos de Construtores e de Pilotos na mesma temporada";
      case "king_of_the_midfield":
        return "Vença um campeonato com um diretor ou chassi de raridade Normal";
      case "one_two":
        return "Faça uma dobradinha com seus dois carros em P1 e P2 em uma mesma corrida";
      case "total_dominance":
        return "Vença o título de Construtores com seus pilotos terminando em P1 e P2 na classificação";
      case "unbreakable":
        return "Vença o título de Construtores sem nenhum DNF dos seus dois carros na temporada inteira";
      case "national_pride":
        return "Vença o título de Construtores com os dois pilotos da mesma nação";
      case "late_charge":
        return "Vença as 2 últimas corridas da temporada";
      case "pace_master":
        return "Vença 4 corridas em uma única temporada";
      case "perfect_harmony":
        return "Vença um campeonato com 3 ou mais sinergias ativas ao mesmo tempo";
      case "goat":
        return "Vença os dois campeonatos em uma temporada com os Modos Caos e Almanaque ativados ao mesmo tempo";
      case "nation_bra":
        return "Vença o título de Construtores com dois pilotos brasileiros";
      case "nation_gbr":
        return "Vença o título de Construtores com dois pilotos britânicos";
      case "nation_ger":
        return "Vença o título de Construtores com dois pilotos alemães";
      case "nation_fra":
        return "Vença o título de Construtores com dois pilotos franceses";
      case "nation_arg":
        return "Vença o título de Construtores com dois pilotos argentinos";
      case "nation_esp":
        return "Vença o título de Construtores com dois pilotos espanhóis";
      case "nation_fin":
        return "Vença o título de Construtores com dois pilotos finlandeses";
      case "nation_aus":
        return "Vença o título de Construtores com dois pilotos australianos";
      case "nation_aut":
        return "Vença o título de Construtores com dois pilotos austríacos";
      case "nation_ita":
        return "Vença o título de Construtores com dois pilotos italianos — a Itália não tem Lendas nem Míticos";
      case "nation_mex":
        return "Vença o título de Construtores com dois pilotos mexicanos — o México não tem Lendas nem Míticos";
      case "nation_can":
        return "Vença o título de Construtores com dois pilotos canadenses — o Canadá não tem Lendas nem Míticos";
    }
  },
  badgeGroupName: (group) => {
    switch (group) {
      case "championship":
        return "Campeonatos";
      case "mastery":
        return "Maestria";
      case "nation":
        return "Nações";
    }
  },

  nameTitle: "Dê um nome à sua equipe",
  nameSub: "Esta é a construtora que você levará ao campeonato.",
  namePlaceholder: "Apex Racing",
  nameHint: "Até 28 caracteres. Deixe em branco para um nome padrão.",
  chaosMode: "Modo Caos",
  chaosDesc: (chaos, calm) =>
    `— oscilações de sorte de ±${chaos}% por corrida. Desligado = domínio da habilidade ±${calm}%.`,
  memoryMode: "Modo de Almanaque",
  memoryDesc: "— esconde os atributos das cartas no draft; escolha pelo seu conhecimento de corridas.",
  startDrafting: "Começar o draft →",
  back: "Voltar",
  teamLabel: "Equipe",
  editName: "Editar nome",
  nameSave: "Salvar",

  driver1: "Piloto 1",
  driver2: "Piloto 2",
  chassis: "Chassi",
  attrs: {
    pace: { abbr: "RIT", full: "Ritmo" },
    consistency: { abbr: "CON", full: "Consistência" },
    rain: { abbr: "CHU", full: "Chuva" },
    motor: { abbr: "MOT", full: "Motor" },
    aero: { abbr: "AER", full: "Aerodinâmica" },
  },
  empty: "— vazio —",
  premiumPack: "PACOTE PREMIUM",
  normalPack: "PACOTE NORMAL",
  draftMeta: (pct, mythics) =>
    `${pct}% premium nesta rodada · ${mythics} mítica${mythics === 1 ? "" : "s"} guardada${mythics === 1 ? "" : "s"}`,
  rollTitle: (n) => `Rodada ${n}`,
  keepOne: (need) => `Fique com uma carta. Você ainda precisa de ${need}.`,
  needDriverOrChassis: "um piloto ou um chassi",
  needDrivers: (n) => `mais ${n} piloto${n > 1 ? "s" : ""}`,
  needChassis: "um chassi",
  fromMemoryBadge: "ALMANAQUE",
  rerollFive: (n) => `↻ Rerolar estas cinco (${n})`,
  startOver: "Recomeçar",
  startOverConfirm: "Recomeçar? Seu draft atual será descartado.",
  lockHint: "Rerole o pacote inteiro, ou rerole a partir de uma carta para manter a equipe ou o país.",
  keepThis: "Escolher",
  slotFull: "Vaga cheia",
  rerollByTeam: (v) => `↻ Rerolar por equipe: ${v}`,
  rerollByCountry: (v) => `↻ Rerolar por país: ${v}`,
  chassisMeta: "Chassi",

  director: "Diretor",
  directorTitle: "Escolha seu diretor de equipe",
  directorSub:
    "Sua equipe está montada. Escolha um diretor — a carta final — para moldar o desempenho do time inteiro.",
  directorIndependent: "Independente",
  directorName: (name) =>
    ({
      "Technical Chief": "Chefe Técnico",
    })[name] ?? name,
  directorTrait: (tr) => {
    switch (tr.kind) {
      case "strategist":
        return `Estrategista — +${tr.paceBonus} de ritmo de corrida`;
      case "qualifier":
        return `Classificação — +${tr.gridBonus} de grid em toda pista (melhor em circuitos lentos)`;
      case "engineer":
        return `Engenheiro — reduz o risco de DNF em ${Math.round(tr.dnfCut * 100)} pts (mais na chuva)`;
      case "motivator":
        return `Motivador — amplifica a sinergia ×${tr.synergyMult}`;
      case "gambler":
        return `Apostador — sorte ×${tr.luckMult}, +${tr.scoreBonus} de base (alto risco)`;
      case "rainMaster":
        return `Mestre da chuva — +${tr.wetBonus} no molhado e mantém o carro na pista`;
      case "developer":
        return `Desenvolvedor — cresce até +${tr.rampMax} ao longo da temporada`;
      case "underdog":
        return `Azarão — sinergia ×${tr.synergyMult} com ${tr.maxMythics} mítica ou menos`;
    }
  },
  directorTraitTip: (tr) => {
    switch (tr.kind) {
      case "strategist":
        return "Estrategista — decisões mais afiadas de corrida elevam o ritmo do time; o ganho cresce com o quanto a pista premia ritmo (grande em Spa/Monza, pequeno em Mônaco).";
      case "qualifier":
        return "Classificação — um bônus fixo de posição de grid que não se dilui em circuitos lentos de rua, onde a posição vale mais que o ritmo puro. Supera um estrategista de corrida num calendário cheio de circuitos de rua.";
      case "engineer":
        return "Engenheiro — foco em confiabilidade que reduz o risco de abandono do time em qualquer condição, sobretudo no molhado.";
      case "motivator":
        return "Motivador — extrai mais de uma equipe que já tem sinergia.";
      case "gambler":
        return "Apostador — oscilações de sorte maiores e um bônus de base: alto risco, alto retorno.";
      case "rainMaster":
        return "Mestre da chuva — grande bônus de pontuação no molhado e muito menos abandonos na chuva; transforma corridas com chuva.";
      case "developer":
        return "Desenvolvedor — o carro melhora ao longo das 10 corridas da temporada.";
      case "underdog":
        return "Azarão — recompensa um elenco barato e entrosado, com poucas ou nenhuma mítica.";
    }
  },
  directorAttrs: {
    reliability: { abbr: "CNF", full: "Confiabilidade" },
    development: { abbr: "DES", full: "Desenvolvimento" },
  },
  directorAttrTip: (key) =>
    key === "reliability"
      ? "Confiabilidade — estabiliza o time contra oscilações de sorte e abandonos. Negativa para diretores agressivos que a trocam por ritmo."
      : "Desenvolvimento — o quanto o time evolui ao longo das 10 corridas da temporada.",
  directorPreviewTitle: "No seu time",
  directorPreviewScore: (n) => `⚡ ${signed(n)} pontuação`,
  directorPreviewWet: (n) => `🌧 +${n} na chuva`,
  directorPreviewDnf: (n) => `🛡 ${signed(n)}% DNF`,
  directorPreviewLuck: (pct) => `🎲 ${signed(pct)}% de oscilação`,
  directorPreviewCar: (motor, aero) => `🔧 +${motor} MOT +${aero} AER carro de casa`,
  directorPreviewNone: "sem efeito perceptível neste time",
  directorPreviewTip:
    "Efeito medido deste chefe nos SEUS dois pilotos + chassi, comparado a não ter diretor — ritmo por carro, bônus na chuva, variação no risco de abandono e o quanto a oscilação da sorte aumenta ou diminui. Qualquer afinidade de casa/independente já está embutida nesses números.",
  directorPreviewAffinity: (pct, home) => (home ? `◇ +${pct}% em casa` : `◇ +${pct}% qualquer carro`),
  directorAffinityTip: (home) =>
    home
      ? "Carro de casa — este chefe dirigiu esta equipe: o desempenho dele recebe o bônus total de casa e ele desenvolve o próprio carro (+motor & aero)."
      : "Independente — sem equipe de origem, mas com uma vantagem menor que vale em qualquer carro.",
  calendarTitle: "Calendário da temporada",
  calendarLegend: "Circuitos lentos favorecem um especialista em classificação; circuitos de potência favorecem um estrategista de corrida.",
  paceTierLabel: (tier) => (tier === "low" ? "lento" : tier === "power" ? "potência" : "equilibrado"),
  paceTierTip: (tier) =>
    tier === "low"
      ? "Circuito lento — a posição de largada é decisiva, então um especialista em classificação se destaca aqui."
      : tier === "power"
        ? "Circuito de potência — o ritmo puro importa mais, então o bônus de ritmo de um estrategista compensa."
        : "Circuito equilibrado — nenhum especialista tem vantagem clara.",
  rerollDirectors: (n) => `↻ Rerolar diretores (${n})`,
  keepDirector: "Contratar este diretor",

  seasonRound: (r) => `Temporada — etapa ${r} / 10`,
  nextGp: "Próximo Grande Prêmio",
  projected: (track, d1, p1, d2, p2, swing, chaos) =>
    `Projeção em ${track}: ${d1} ${p1} · ${d2} ${p2} · sorte ±${swing}% (${luckTag(chaos, false)})`,
  runGp: (track) => `▸ Disputar GP de ${track}`,
  abandon: "Abandonar",
  viewChampionship: "Ver campeonato →",
  challengesTitle: "Desafios em andamento",
  yourLineup: "Sua equipe",
  lineupHint: "— atributos em verde incluem bônus de sinergia",
  lineupShow: "Atributos ▾",
  lineupHide: "Ocultar ▴",
  driver: "Piloto",
  noSynergies: "Nenhuma sinergia ativa para esta equipe.",
  fromSynergy: "de sinergia",
  fromHistoric: "da passagem histórica",
  fromCarDev: "diretor desenvolve o carro",
  thPos: "Pos",
  thPosShort: "P",
  thEntry: "Inscrito",
  thConstructor: "Construtora",
  thPts: "Pts",
  thDriver: "Piloto",
  thLineup: "Equipe",
  dnf: "DNF",
  yourResult: (pos, driver, points) => `Seu resultado: ${pos} — ${driver}${points ? ` · ${points} pts` : ""}`,
  standingsTitle: "Classificação do campeonato",
  tabConstructors: "Construtores",
  tabDrivers: "Pilotos",

  wccChampion: "Construtor Campeão Mundial",
  wdcChampion: "Piloto Campeão Mundial",
  titleSecured: "Título conquistado — adicionado ao seu histórico.",
  noTitle: "Sem título desta vez — tente de novo.",
  memoryWin: "🧠 Campeão de Almanaque — você venceu com os atributos das cartas ocultos!",
  newSeason: "Nova temporada →",
  shareResult: "⤴ Compartilhar resultado",
  sharePreparing: "Preparando imagem…",
  shareSaved: "Imagem salva ✓",
  shareShared: "Compartilhado ✓",
  shareUnavailable: "Compartilhamento indisponível",
  shareModalTitle: "Sua temporada",
  shareVia: "⤴ Compartilhar…",
  shareCopy: "⧉ Copiar imagem",
  shareCopied: "Copiada ✓",
  shareCopyFailed: "Falha ao copiar",
  shareDownload: "↓ Baixar",
  shareClose: "Fechar",

  imgSeasonComplete: "TEMPORADA CONCLUÍDA",
  imgDaily: (day) => `DIÁRIO · ${day}`,
  imgWccChampion: "CONSTRUTOR CAMPEÃO MUNDIAL",
  imgWdcChampion: "PILOTO CAMPEÃO MUNDIAL",
  imgTitleSecured: "★ TÍTULO CONQUISTADO ★",
  imgNoTitle: "SEM TÍTULO DESTA VEZ",
  imgMemoryWin: "🧠 CAMPEÃO DE ALMANAQUE",
  imgChaosWin: "⚡ CAMPEÃO NO MODO CAOS",
  imgPositions: (wccPos, pts) => `Construtores P${wccPos} · ${pts} pts`,
  imgSynergies: "SINERGIAS",
  imgFooter: "Monte uma temporada lendária de corridas — jogue a sua",
  imgEmblems: "MEUS EMBLEMAS",
  imgEmblemsCount: (n, total) => `${n} de ${total} desbloqueados`,
  shareBadgesCaption: (n, total) => `Desbloqueei ${n}/${total} emblemas no Grand Prix Draft Legends! 🏅`,
  shareCaptionWon: (team) => `${team} é Campeã Mundial no Grand Prix Draft Legends! 🏆`,
  shareCaptionLost: (team) => `Minha temporada no Grand Prix Draft Legends com a ${team}.`,
  shareCaptionDaily: (team, won, wccPos, teamPts, driverPts) =>
    won
      ? `${team} foi campeão no Desafio Diário do Grand Prix Draft Legends! 🏆 Construtores P${wccPos}, ${teamPts} pts (pilotos ${driverPts} pts). O mesmo grid para todos — será que você consegue também?`
      : `${team} terminou em Construtores P${wccPos} (${teamPts} pts · pilotos ${driverPts} pts) no Desafio Diário do Grand Prix Draft Legends. O mesmo grid para todos — será que você consegue superar?`,
  shareDailyTitle: "Desafie um amigo",
  shareCopyMessage: "⧉ Copiar mensagem",

  lbTitle: "Ranking diário",
  lbLoading: "Carregando os tempos de hoje…",
  lbEmpty: "Nenhuma pontuação ainda — seja o primeiro.",
  lbYou: "Você",
  lbTeam: "Equipe",
  lbNoticeTitle: "Enviado ao ranking",
  lbNoticeBody:
    "Seu resultado diário e o nome da equipe agora aparecem no ranking diário público. Você pode parar de enviar resultados futuros quando quiser.",
  lbNoticeOptOut: "Não enviar meus resultados",
  lbNoticeOk: "Entendi",

  rarity: (r) => (r === "legend" ? "lendária" : r === "mythic" ? "mítica" : "normal"),
  synReason: (r) => {
    switch (r.kind) {
      case "historicDrive":
        return `${r.driver} ↔ ${r.team} (passagem histórica)`;
      case "sameNation":
        return `Mesma nação (${r.country}) — química de dupla`;
      case "sharedTeam":
        return `${r.a} & ${r.b} já foram companheiros de equipe`;
    }
  },
  synTag: (t) => {
    switch (t.kind) {
      case "droveFor":
        return `Pilotou pela ${t.team}`;
      case "sameNationAs":
        return `Mesma nação de ${t.driver} (${t.country})`;
      case "exTeammate":
        return `Ex-companheiro de ${t.driver}`;
      case "chassisDroveFor":
        return `${t.driver} pilotou pela ${t.team}`;
    }
  },
  trackLabel: (label) => TRACKS_PT[label] ?? label,
  weather: (w) => (w === "heavy_rain" ? "Chuva forte" : w === "light_rain" ? "Chuva fraca" : "Seco"),
  traitTip: (key) => {
    switch (key) {
      case "motor":
        return "Potência do motor é o que mais importa aqui";
      case "aero":
        return "Aerodinâmica/downforce é o que mais importa aqui";
      case "pace":
        return "Ritmo do piloto é o que mais importa aqui";
      case "consistency":
        return "Consistência do piloto é o que mais importa aqui";
      case "balanced":
        return "Equilibrado — nenhum atributo se destaca";
    }
  },
  weatherTip: (w) =>
    w === "heavy_rain"
      ? "Chuva forte — a habilidade na chuva pesa muito, risco de abandono alto"
      : w === "light_rain"
        ? "Chuva fraca — a habilidade na chuva começa a pesar, risco de abandono sobe um pouco"
        : "Seco — a habilidade na chuva não tem efeito",
};

const DICTS: Record<Lang, Dict> = { en, "pt-BR": ptBR };

const LANG_KEY = "f1dl_lang";

function detect(): Lang {
  try {
    const saved = localStorage.getItem(LANG_KEY);
    if (saved === "en" || saved === "pt-BR") return saved;
  } catch {
    /* ignore */
  }
  const nav = (typeof navigator !== "undefined" ? navigator.language : "").toLowerCase();
  return nav.startsWith("pt") ? "pt-BR" : "en";
}

let current: Lang = detect();

export function getLang(): Lang {
  return current;
}

export function setLang(lang: Lang): void {
  current = lang;
  try {
    localStorage.setItem(LANG_KEY, lang);
  } catch {
    /* ignore */
  }
  if (typeof document !== "undefined") document.documentElement.lang = lang;
}

/** The active dictionary. Call fresh on each render so a language switch applies. */
export function t(): Dict {
  return DICTS[current];
}
